import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  TablePagination,
} from '@mui/material';
import { Add, Visibility, AssignmentOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../../components/PageHeader';
import StatCard from '../../dashboard/components/StatCard';
import LoadingLogo from '../../../components/LoadingLogo';
import EstadoChip from '../components/EstadoChip';
import PrioridadChip from '../components/PrioridadChip';
import SolicitudFormModal from '../components/SolicitudFormModal';
import sigcodService from '../services/sigcodService';

const ESTADOS = [
  '', 'CREADA', 'OBSERVADA', 'ENVIADA_A_BODEGA', 'EN_PROCESO',
  'LISTA_PARA_ENVIO', 'EN_RUTA', 'ENTREGADA_AL_CLIENTE', 'COMPARTIDA', 'CERRADA', 'CANCELADA',
];

const SolicitudesPage = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [filters, setFilters] = useState({ estado: '', tipo: '', prioridad: '', search: '' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = { page: page + 1, limit: rowsPerPage };
      if (filters.estado) params.estado = filters.estado;
      if (filters.tipo) params.tipo = filters.tipo;
      if (filters.prioridad) params.prioridad = filters.prioridad;
      if (filters.search) params.search = filters.search;

      const [reqRes, statsRes] = await Promise.all([
        sigcodService.getRequests(params),
        sigcodService.getStats(),
      ]);
      setRequests(reqRes.data || []);
      setTotal(reqRes.pagination?.total || 0);
      setStats(statsRes.data || {});
    } catch (error) {
      setSnackbar({ open: true, message: 'Error al cargar solicitudes', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [page, rowsPerPage, filters]);

  const handleCreate = async (data) => {
    await sigcodService.createRequest(data);
    setSnackbar({ open: true, message: 'Solicitud creada exitosamente', severity: 'success' });
    loadData();
  };

  const formatDate = (date) =>
    new Date(date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });

  if (loading && requests.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <LoadingLogo size={120} />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Solicitudes SIGCOD"
        subtitle="Gestion de solicitudes de documentos"
        actions={
          <Button variant="contained" startIcon={<Add />} onClick={() => setOpenModal(true)}>
            Nueva Solicitud
          </Button>
        }
      />

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatCard title="Total" value={stats.total || 0} icon={<AssignmentOutlined />} color="#5980a6" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard title="Creadas" value={stats.creadas || 0} icon={<AssignmentOutlined />} color="#1565c0" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard title="En Proceso" value={stats.enProceso || 0} icon={<AssignmentOutlined />} color="#f57f17" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard title="Cerradas" value={stats.cerradas || 0} icon={<AssignmentOutlined />} color="#2e7d32" />
        </Grid>
      </Grid>

      {/* Filters */}
      <Card elevation={0} sx={{ mb: 2, borderRadius: 0, border: '1px solid rgba(29,31,32,0.16)' }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar..."
                value={filters.search}
                onChange={(e) => { setFilters({ ...filters, search: e.target.value }); setPage(0); }}
              />
            </Grid>
            <Grid item xs={4} sm={3}>
              <TextField
                fullWidth
                size="small"
                select
                label="Estado"
                value={filters.estado}
                onChange={(e) => { setFilters({ ...filters, estado: e.target.value }); setPage(0); }}
              >
                <MenuItem value="">Todos</MenuItem>
                {ESTADOS.filter(Boolean).map((e) => (
                  <MenuItem key={e} value={e}>{e.replace(/_/g, ' ')}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={4} sm={3}>
              <TextField
                fullWidth
                size="small"
                select
                label="Tipo"
                value={filters.tipo}
                onChange={(e) => { setFilters({ ...filters, tipo: e.target.value }); setPage(0); }}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="FISICA">Fisica</MenuItem>
                <MenuItem value="DIGITAL">Digital</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={4} sm={3}>
              <TextField
                fullWidth
                size="small"
                select
                label="Prioridad"
                value={filters.prioridad}
                onChange={(e) => { setFilters({ ...filters, prioridad: e.target.value }); setPage(0); }}
              >
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="BAJA">Baja</MenuItem>
                <MenuItem value="MEDIA">Media</MenuItem>
                <MenuItem value="ALTA">Alta</MenuItem>
                <MenuItem value="URGENTE">Urgente</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <Card elevation={0} sx={{ borderRadius: 0, border: '1px solid rgba(29,31,32,0.16)', boxShadow: '0 1px 2px rgba(43,43,45,.14)' }}>
        <CardContent>
          {requests.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <AssignmentOutlined sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No hay solicitudes
              </Typography>
              <Button variant="contained" startIcon={<Add />} onClick={() => setOpenModal(true)}>
                Crear Primera Solicitud
              </Button>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Consecutivo</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Solicitante</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Prioridad</TableCell>
                      <TableCell>Fecha</TableCell>
                      <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {requests.map((req) => (
                      <TableRow key={req._id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/sigcod/solicitudes/${req._id}`)}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} color="primary">
                            {req.consecutivo}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{req.tipo}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{req.solicitante?.nombre}</Typography>
                        </TableCell>
                        <TableCell><EstadoChip estado={req.estado} /></TableCell>
                        <TableCell><PrioridadChip prioridad={req.prioridad} /></TableCell>
                        <TableCell>
                          <Typography variant="caption">{formatDate(req.createdAt)}</Typography>
                        </TableCell>
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Ver Detalle">
                            <IconButton size="small" color="primary" onClick={() => navigate(`/sigcod/solicitudes/${req._id}`)}>
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
                rowsPerPageOptions={[10, 20, 50]}
                labelRowsPerPage="Filas"
              />
            </>
          )}
        </CardContent>
      </Card>

      <SolicitudFormModal open={openModal} onClose={() => setOpenModal(false)} onSubmit={handleCreate} />

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default SolicitudesPage;
